import { ReactNode, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ChevronRight } from "lucide-react";
import { GardenPhase } from "@/lib/gardenStatus";
import { GardenStatusBadge } from "./GardenStatusBadge";

interface DecisionCardProps {
  /** The decision itself — verb-first ("Assign Kohl to Chess Club"). */
  decision: ReactNode;
  /** One-sentence plain-language explanation of why this decision is being surfaced. */
  because: ReactNode;
  /** Garden phase for this card. */
  phase?: GardenPhase;
  /** The primary action label. */
  primaryLabel: string;
  onPrimary: () => void;
  /** Optional secondary (usually "Not now" or "Dismiss"). */
  secondaryLabel?: string;
  onSecondary?: () => void;
  /** Evidence — the "math" behind the decision. Hidden by default; revealed via "Show the math". */
  evidence?: ReactNode;
  /** Audit trail — who/what/when. Hidden by default. */
  audit?: ReactNode;
}

/**
 * The Decision-First card. Order is non-negotiable:
 *   1. The decision
 *   2. Short explanation
 *   3. Primary action
 *   4. Supporting evidence (progressive disclosure)
 *   5. Audit trail (progressive disclosure)
 *
 * See mem/constitution/decision-first.md.
 */
export function DecisionCard({
  decision,
  because,
  phase = "needs_care",
  primaryLabel,
  onPrimary,
  secondaryLabel,
  onSecondary,
  evidence,
  audit,
}: DecisionCardProps) {
  const [showMath, setShowMath] = useState(false);

  return (
    <article className="paper-card-lift p-5 md:p-6 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1.5">
          <GardenStatusBadge phase={phase} />
          <h3 className="font-heading text-lg md:text-xl text-foreground leading-snug">
            {decision}
          </h3>
          <p className="text-sm text-muted-foreground">{because}</p>
        </div>
      </div>

      <div className="flex items-center gap-2 pt-1">
        <Button onClick={onPrimary} className="gap-1">
          {primaryLabel}
          <ChevronRight className="h-4 w-4" />
        </Button>
        {secondaryLabel && onSecondary && (
          <Button variant="ghost" onClick={onSecondary}>
            {secondaryLabel}
          </Button>
        )}
        {(evidence || audit) && (
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto text-xs text-muted-foreground"
            onClick={() => setShowMath(true)}
          >
            Show the math
          </Button>
        )}
      </div>

      {(evidence || audit) && (
        <Dialog open={showMath} onOpenChange={setShowMath}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                Behind this decision
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 text-sm">
              {evidence && (
                <section>
                  <div className="eyebrow mb-2">Evidence</div>
                  <div className="text-foreground/90 space-y-2">{evidence}</div>
                </section>
              )}
              {audit && (
                <section>
                  <div className="eyebrow mb-2">Audit trail</div>
                  <div className="text-muted-foreground space-y-1">{audit}</div>
                </section>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </article>
  );
}
